/**
 * shadowAggregator - Jahres-Sonnenstunden-Grid + Zonen (#606)
 *
 * Reine, WebGL-/Fabric-freie Aggregation (Jest-testbar). Über eine feste
 * Stichprobe von Sonnenständen (stündlich 7–19 Uhr am 15. jedes Monats
 * März–Oktober) wird pro Rasterzelle die mittlere direkte Sonnenzeit pro Tag
 * berechnet und gärtnerisch klassifiziert:
 *   vollsonnig ≥ 6 h · halbschattig 3–6 h · schattig < 3 h.
 *
 * Der Belaubungsfaktor jedes Schattens (foliage) geht als Teil-Beschattung ein:
 * eine nur von einem kahlen Laubbaum (Gewicht 0.2) getroffene Zelle bekommt in
 * dieser Stunde 80 % Sonne. So zählt „Sonnenstunden" die direkte Sonne, gewichtet
 * nach Belaubung — keine Diffuslicht-Modellierung (bewusste Vereinfachung).
 *
 * Läuft im Web Worker (heavy), ist hier aber synchron aufrufbar (Tests +
 * Fallback ohne Worker).
 */

import { getSunPosition, getSunDirection3D } from '../sunlight';
import { hashString } from '../plantArchetypes';
import { createShadowTerrain } from '../terrain';
import { computeShadowGeometry, MIN_SUN_ALTITUDE_RAD } from './shadowCaster';

const MIN_SUN_Y = Math.sin(MIN_SUN_ALTITUDE_RAD);

// Vegetationsperiode: 15. jedes Monats März–Oktober (0-indexiert 2–9) = 8 Tage
export const AGGREGATION_MONTHS = [2, 3, 4, 5, 6, 7, 8, 9];
export const AGGREGATION_HOURS = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19];

export const DEFAULT_CELL_SIZE_M = 0.25;
export const MAX_CELLS = 50000;

// Gärtnerische Standard-Schwellen (mittlere direkte Sonnenstunden/Tag)
export const FULL_SUN_HOURS = 6;
export const PARTIAL_SHADE_HOURS = 3;

/** Zonen-Klasse aus mittleren Sonnenstunden/Tag. */
export function classifySunHours(hours) {
  if (hours >= FULL_SUN_HOURS) return 'full';
  if (hours >= PARTIAL_SHADE_HOURS) return 'partial';
  return 'shade';
}

/** Achsen-Bounding-Box einer MultiPolygon ([x,z]-Paare). */
function multiPolygonBounds(multiPolygon) {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const polygon of multiPolygon) {
    for (const ring of polygon) {
      for (const [x, z] of ring) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (z < minZ) minZ = z;
        if (z > maxZ) maxZ = z;
      }
    }
  }
  return { minX, maxX, minZ, maxZ };
}

/** Even-odd-Punkttest über alle Ringe einer MultiPolygon. */
function pointInMultiPolygon(multiPolygon, x, z) {
  let inside = false;
  for (const polygon of multiPolygon) {
    for (const ring of polygon) {
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
        const xi = ring[i][0];
        const zi = ring[i][1];
        const xj = ring[j][0];
        const zj = ring[j][1];
        const intersect = zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi;
        if (intersect) inside = !inside;
      }
    }
  }
  return inside;
}

/** Sonnenstände der Stichprobe → Schattengeometrie je Sample. */
function buildSamples(sceneModel, latitude, longitude, year) {
  const northAngle = sceneModel.meta?.northAngle ?? 0;
  // Terrain-Sampler EINMAL rekonstruieren (#631) — gilt für alle 104 Sonnenstände.
  const terrain = createShadowTerrain(sceneModel.meta?.terrainSpec);
  const samples = [];
  for (const month of AGGREGATION_MONTHS) {
    for (const hour of AGGREGATION_HOURS) {
      const date = new Date(year, month, 15, hour, 0, 0);
      const { azimuth, altitude } = getSunPosition(latitude, longitude, date);
      if (Math.sin(altitude) <= MIN_SUN_Y) continue; // Nacht/Dämmerung
      const sunDirection = getSunDirection3D(azimuth, altitude, northAngle);
      const { groups } = computeShadowGeometry(sceneModel, { sunDirection, month, terrain });
      samples.push(
        groups.map((g) => ({ weight: g.weight, mp: g.multiPolygon, bounds: multiPolygonBounds(g.multiPolygon) }))
      );
    }
  }
  return samples;
}

/**
 * Jahres-Sonnenstunden-Grid für einen Garten.
 *
 * @param {object} sceneModel - SceneModel (#600); braucht meta.boundsM
 * @param {object} options
 * @param {number} options.latitude
 * @param {number} options.longitude
 * @param {number} [options.cellSizeM=0.25]
 * @param {number} [options.year] - Bezugsjahr (Default: aktuelles)
 * @param {(fraction:number)=>void} [options.onProgress]
 * @returns {{cellSizeM:number, cols:number, rows:number, origin:{x:number,z:number},
 *            sunHours:Float32Array, dayCount:number, sampleCount:number}|null}
 */
export function aggregateSunlight(sceneModel, {
  latitude,
  longitude,
  cellSizeM = DEFAULT_CELL_SIZE_M,
  year = new Date().getFullYear(),
  onProgress,
} = {}) {
  const bounds = sceneModel?.meta?.boundsM;
  if (!bounds || !(bounds.width > 0) || !(bounds.depth > 0)) return null;

  // Zellgröße wächst bei Riesengärten, damit die Zellzahl gedeckelt bleibt
  let cell = cellSizeM;
  let cols = Math.max(1, Math.ceil(bounds.width / cell));
  let rows = Math.max(1, Math.ceil(bounds.depth / cell));
  while (cols * rows > MAX_CELLS) {
    cell *= 1.5;
    cols = Math.max(1, Math.ceil(bounds.width / cell));
    rows = Math.max(1, Math.ceil(bounds.depth / cell));
  }

  const samples = buildSamples(sceneModel, latitude, longitude, year);
  const dayCount = AGGREGATION_MONTHS.length;
  const sunHours = new Float32Array(cols * rows);

  for (let s = 0; s < samples.length; s += 1) {
    const groups = samples[s];
    for (let r = 0; r < rows; r += 1) {
      const cz = bounds.minZ + (r + 0.5) * cell;
      for (let c = 0; c < cols; c += 1) {
        const cx = bounds.minX + (c + 0.5) * cell;
        // Maximales Beschattungs-Gewicht der Zelle in dieser Stunde
        let shade = 0;
        for (let g = 0; g < groups.length && shade < 1; g += 1) {
          const grp = groups[g];
          if (grp.weight <= shade) continue; // kann das Maximum nicht heben
          const b = grp.bounds;
          if (cx < b.minX || cx > b.maxX || cz < b.minZ || cz > b.maxZ) continue;
          if (pointInMultiPolygon(grp.mp, cx, cz)) shade = grp.weight;
        }
        sunHours[r * cols + c] += 1 - shade; // 1 Stunde je Sample
      }
    }
    onProgress?.((s + 1) / samples.length);
  }

  // Mittlere Sonnenstunden pro Tag über die Vegetationsperiode
  for (let i = 0; i < sunHours.length; i += 1) sunHours[i] /= dayCount;

  return {
    cellSizeM: cell,
    cols,
    rows,
    origin: { x: bounds.minX, z: bounds.minZ },
    sunHours,
    dayCount,
    sampleCount: samples.length,
  };
}

/**
 * Kompakter Hash der schattenrelevanten Geometrie + Standort. Ändert er sich
 * nicht, ist ein zwischengespeichertes Grid weiter gültig (kein Neurechnen).
 */
export function sceneShadowHash(sceneModel, coords) {
  const parts = [
    'n',
    Math.round((sceneModel?.meta?.northAngle ?? 0) * 10),
    'c',
    Math.round((coords?.latitude ?? 0) * 1000),
    Math.round((coords?.longitude ?? 0) * 1000),
    // Terrain ändert die Schattengeometrie (#631) → Cache invalidieren.
    't',
    sceneModel?.meta?.terrainSpec ? JSON.stringify(sceneModel.meta.terrainSpec) : '0',
  ];
  for (const ex of sceneModel?.extrusions ?? []) {
    if (!ex.castsShadow) continue;
    parts.push('E', Math.round(ex.heightM * 100), Math.round((ex.baseY ?? 0) * 100));
    for (const p of ex.footprint ?? []) {
      parts.push(Math.round(p.x * 100), Math.round(p.z * 100));
    }
  }
  for (const pl of sceneModel?.plants ?? []) {
    if (!pl.castsShadow) continue;
    parts.push(
      'P',
      pl.archetype,
      pl.evergreen ? 1 : 0,
      Math.round(pl.heightM * 100),
      Math.round(pl.crownDiameterM * 100),
      Math.round(pl.position.x * 100),
      Math.round(pl.position.z * 100)
    );
  }
  return hashString(parts.join(','));
}

/** Mittlere Sonnenstunden/Tag an einem Weltpunkt (Meter) aus dem Grid. */
export function sunHoursAt(grid, point) {
  if (!grid) return null;
  const c = Math.floor((point.x - grid.origin.x) / grid.cellSizeM);
  const r = Math.floor((point.z - grid.origin.z) / grid.cellSizeM);
  if (c < 0 || c >= grid.cols || r < 0 || r >= grid.rows) return null;
  return grid.sunHours[r * grid.cols + c];
}

/** Zonen-Klasse ('full'|'partial'|'shade') an einem Weltpunkt. */
export function zoneAt(grid, point) {
  const hours = sunHoursAt(grid, point);
  return hours == null ? null : classifySunHours(hours);
}
